import React from 'react';
import { showErrorToast, showToast } from 'src/utils';

export interface IPendingTransition {
  status: string;
  stepIndex: number;
  stepLabel: string;
}

/**
 * @registry Holds the deferred workflow transition while its notification composer is open:
 * opens the composer, loads the preview for the event, and drops the pending transition
 * (with a "status unchanged" toast) when the user backs out. The caller submits the
 * transition from `pending` once the composer sends or skips.
 */
export function useTransitionComposer(
  loadPreview: (eventType: string) => Promise<unknown | null>,
) {
  const [opened, setOpened] = React.useState(false);
  const [pending, setPending] = React.useState<IPendingTransition | null>(null);

  const open = async (
    transition: IPendingTransition,
    eventType: string,
  ): Promise<void> => {
    setPending(transition);
    setOpened(true);
    const preview = await loadPreview(eventType);
    if (!preview) {
      setOpened(false);
      setPending(null);
      showErrorToast(
        'Unable to load the notification preview. Please try again.',
      );
    }
  };

  const close = () => {
    setOpened(false);
    setPending(null);
  };

  const cancel = () => {
    close();
    showToast('Submission canceled — status unchanged');
  };

  return { opened, pending, open, close, cancel };
}
